import {
  getAgGridColId,
  isMultileg,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type Security,
} from '@talos/kyoko';
import type { GridApi, IRowNode } from 'ag-grid-community';
import { compact, max, values } from 'lodash-es';
import { useMemo } from 'react';
import type { PriceBlotterEntity } from './PriceBlotterEntity';
import type { PriceBlotterColumn } from './types';
import {
  getLegPriceColumn,
  getPriceColumn,
  usePriceBlotterColumnDynamicsSpecs,
  type PriceBlotterColumnDynamicsSpecType,
} from './usePriceBlotterColumnDynamics';

const SECURITY_MARKET_DATA_LEG_1_COL_ID = 'SecurityMarketData-Leg1';
const SECURITY_MARKET_DATA_SYMBOL_COL_ID = 'SecurityMarketData-Symbol';
const SECURITY_MARKET_DATA_LEG_2_COL_ID = 'SecurityMarketData-Leg2';

export const usePriceBlotterColumns = (defaultColumns: PriceBlotterColumn[], enableColumnDynamics: boolean) => {
  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      compact([
        {
          type: 'security',
          field: 'security.Symbol',
          id: 'Symbol',
          title: 'Symbol',
          initialWidth: 250,
        },
        {
          type: 'productType',
          field: 'security.Symbol',
          id: 'ProductType',
          title: 'Product Type',
          initialRowGroup: true,
          initialHide: true,
        },
        {
          type: 'security',
          id: 'Leg1Symbol',
          title: 'Leg 1',
          field: 'security.MultilegDetails.Legs[0].Symbol',
          initialHide: true,
        },
        {
          type: 'security',
          id: 'Leg2Symbol',
          title: 'Leg 2',
          field: 'security.MultilegDetails.Legs[1].Symbol',
          initialHide: true,
        },
        {
          ...getLegPriceColumn({
            colID: SECURITY_MARKET_DATA_LEG_1_COL_ID,
            spec: 'leg-price' satisfies PriceBlotterColumnDynamicsSpecType,
            removable: false,
            initialState: {
              title: 'Leg 1 Price',
              legIndex: 0,
            },
          }),
          initialHide: true,
        },
        getPriceColumn({
          colID: SECURITY_MARKET_DATA_SYMBOL_COL_ID,
          spec: 'price' satisfies PriceBlotterColumnDynamicsSpecType,
          removable: false,
          initialState: {},
        }),
        {
          ...getLegPriceColumn({
            colID: SECURITY_MARKET_DATA_LEG_2_COL_ID,
            spec: 'leg-price' satisfies PriceBlotterColumnDynamicsSpecType,
            removable: false,
            initialState: {
              title: 'Leg 2 Price',
              legIndex: 1,
            },
          }),
          initialHide: true,
        },
        {
          type: 'expandRow',
          id: 'expandRow',
          initialHide: false,
          params: {
            // This function can and should become a lot smarter in the future.
            getExpandedHeight: (node: IRowNode<PriceBlotterEntity>, api: GridApi<Security>) => {
              const pbe = node.data;
              if (!pbe) {
                return undefined;
              }

              const marketDataLevels = getMostLevelsFound(pbe) ?? 0;

              /**
               * You always want to be able to expand a multileg row because you want to see the legs printed underneath the security.
               * And then you also want to be able to expand any row that has more than one market data level available for show.
               */
              if (isMultileg(pbe.security) || marketDataLevels > 1) {
                return 80; // this number needs to become smarter in the future but works for now
              }

              // Else, return undefined. This means that there's nothing to expand and show, so the button wont show.
              return undefined;
            },
          },
        },
      ] satisfies ColumnDef<any>[]).map(c => [getAgGridColId(c), c])
    );
  }, []);

  // useDefaultColumns just applies like hide and unhide stuff based on the defaultColumns list
  const columns = useDefaultColumns(defaultColumns, columnDefinitions);
  const { columnDynamicsSpecs, dialogs } = usePriceBlotterColumnDynamicsSpecs();

  if (!enableColumnDynamics) {
    return {
      columns,
      columnDynamicsSpecs: [],
      dialogs: null,
    };
  }

  return { columns, columnDynamicsSpecs, dialogs };
};

/**
 * Given a security enriched with a marketdata structure, returns
 * the highest bid or offer levels found (amount of levels)
 * @param sec
 */
function getMostLevelsFound(pbe: PriceBlotterEntity) {
  return max(values(pbe.marketData).flatMap(mds => [mds.Bids.length, mds.Offers.length]));
}
